import { useEffect, useState, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const STORAGE_KEY = 'forceRefreshBaselineAt';
// 60s poll feels right post-deploy without being chatty.
const POLL_INTERVAL_MS = 60_000;
// Auto-reload countdown after the banner appears.
const COUNTDOWN_SECONDS = 30;

/**
 * Polls /api/refresh-check every 60s. When the server's force_refresh_at is
 * newer than what this tab last saw, a non-dismissable banner appears with a
 * 30s countdown and a Refresh Now button. After the countdown, the page
 * auto-reloads (window.location.reload(true)).
 *
 * Backwards compat: a fresh DB returns null, which we treat as "no push yet"
 * and quietly store as the baseline so legacy users don't see the banner
 * the first time the feature ships.
 */
export default function ForceRefreshBanner() {
  const [pendingPush, setPendingPush] = useState(null); // { force_refresh_at, pushed_by_name, note }
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  // Read baseline from sessionStorage so it survives across in-app navigations
  // (the banner remounts inside the Layout when routes change). Empty string
  // means "no value yet" - first successful poll establishes it.
  const baselineRef = useRef((() => {
    try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
  })());

  useEffect(() => {
    let cancelled = false;
    let pollHandle = null;

    const checkOnce = async () => {
      try {
        const r = await api.get('/refresh-check');
        const ts = r?.data?.force_refresh_at || null;
        if (cancelled) return;

        // First-ever fetch on this tab: silently establish baseline.
        if (baselineRef.current === null) {
          baselineRef.current = ts || '';
          try { sessionStorage.setItem(STORAGE_KEY, ts || ''); } catch { /* ignore */ }
          return;
        }

        if (ts && ts !== baselineRef.current) {
          // Persist the new baseline IMMEDIATELY - before showing the banner
          // and before any reload. This is the fix for the "spammed banners"
          // bug: the auto-reload path didn't update sessionStorage, so each
          // reload re-detected the same drift and looped.
          baselineRef.current = ts;
          try { sessionStorage.setItem(STORAGE_KEY, ts); } catch { /* ignore */ }
          setPendingPush({
            force_refresh_at: ts,
            pushed_by_name: r.data?.pushed_by_name || '',
            note: r.data?.note || '',
          });
          if (pollHandle) clearInterval(pollHandle);
        }
      } catch { /* network blip - try again next tick */ }
    };

    checkOnce();
    pollHandle = setInterval(checkOnce, POLL_INTERVAL_MS);
    return () => { cancelled = true; if (pollHandle) clearInterval(pollHandle); };
  }, []);

  // Build-drift listener - fires when the axios interceptor sees a new
  // X-App-Build header on any API response. This catches deploys instantly
  // without waiting for the 60s poll, and also catches deploys where the
  // admin didn't manually click "Push Refresh" at all.
  useEffect(() => {
    const onDrift = (e) => {
      // Same UX as a push: show the banner and start the countdown. Use the
      // axios-detected build id as the new baseline so reloads don't loop.
      const newBuild = e?.detail?.to;
      if (newBuild) {
        try { sessionStorage.setItem(STORAGE_KEY, `build:${newBuild}`); } catch { /* ignore */ }
      }
      setPendingPush(prev => prev || {
        force_refresh_at: new Date().toISOString(),
        pushed_by_name: '',
        note: 'A new version of the portal is available.',
      });
    };
    window.addEventListener('app:build-drift', onDrift);
    return () => window.removeEventListener('app:build-drift', onDrift);
  }, []);

  // Countdown + auto-reload once a push is detected.
  useEffect(() => {
    if (!pendingPush) return;
    if (countdown <= 0) {
      // Defensive: ensure baseline is persisted before reloading so the new
      // page load doesn't re-detect the same change. Belt-and-suspenders -
      // the poll handler also persists, but covers any future code paths.
      try { sessionStorage.setItem(STORAGE_KEY, pendingPush.force_refresh_at); } catch { /* ignore */ }
      window.location.reload();
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [pendingPush, countdown]);

  if (!pendingPush) return null;

  const reloadNow = () => {
    // Persist the new baseline so the next tab/load doesn't immediately fire.
    try { sessionStorage.setItem(STORAGE_KEY, pendingPush.force_refresh_at); } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg border border-amber-500/40 bg-zinc-900/95 backdrop-blur shadow-2xl shadow-amber-500/10 overflow-hidden"
      data-testid="force-refresh-banner"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3 p-4">
        <RefreshCw className="h-5 w-5 text-amber-400 mt-0.5 shrink-0 animate-spin" style={{ animationDuration: '3s' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-100">Updates available</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {pendingPush.pushed_by_name
              ? <>Pushed by <span className="text-zinc-200">{pendingPush.pushed_by_name}</span>. </>
              : null}
            Auto-reloading in <span className="text-amber-300 font-mono font-semibold">{countdown}s</span>…
          </p>
          {pendingPush.note && (
            <p className="text-xs text-zinc-300 mt-1.5 italic">&quot;{pendingPush.note}&quot;</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={reloadNow}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 text-zinc-900 font-semibold hover:bg-amber-400 transition-colors"
              data-testid="force-refresh-reload-now"
            >
              <RefreshCw className="h-3 w-3" /> Refresh Now
            </button>
            <span className="text-xs text-zinc-500" title="Save anything you're working on - this page will reload automatically.">
              Save your work - reload is automatic
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
